'use client';

import { useState, useRef, useCallback } from 'react';
import { BREAKPOINTS, type Breakpoint } from '../utils/breakpoints';
import { useResizeObserver } from './useResizeObserver';

interface ViewportState {
  width: number;
  height: number;
  breakpoint: Breakpoint;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isTouchDevice: boolean;
}

export function useViewport(): ViewportState {
  const [viewport, setViewport] = useState<ViewportState>(() => {
    // Server-safe default state to ensure SSR/CSR markup matches on first render
    return {
      width: 0,
      height: 0,
      breakpoint: 'MEDIUM',
      isMobile: false,
      isTablet: false,
      isDesktop: false,
      isTouchDevice: false,
    };
  });

  // Create ref for document.documentElement (SSR-safe)
  const targetRef = useRef<HTMLElement | null>(
    typeof document !== 'undefined' ? document.documentElement : null
  );

  // Stable callback to update viewport state
  const updateViewport = useCallback((width: number, height: number) => {
    const breakpoint = getBreakpoint(width);

    setViewport({
      width,
      height,
      breakpoint,
      isMobile: width < BREAKPOINTS.SMALL,
      isTablet: width >= BREAKPOINTS.SMALL && width < BREAKPOINTS.MEDIUM,
      isDesktop: width >= BREAKPOINTS.MEDIUM,
      isTouchDevice: detectTouchDevice(),
    });
  }, []);

  // Use ResizeObserver hook to track viewport changes
  useResizeObserver(targetRef, updateViewport);

  return viewport;
}

// Helper function to determine the current breakpoint
function getBreakpoint(width: number): Breakpoint {
  if (width >= BREAKPOINTS.XLARGE) return 'XLARGE';
  if (width >= BREAKPOINTS.LARGE) return 'LARGE';
  if (width >= BREAKPOINTS.MEDIUM) return 'MEDIUM';
  if (width >= BREAKPOINTS.SMALL) return 'SMALL';
  if (width >= BREAKPOINTS.XS) return 'XS';
  return 'XXS';
}

// Detect if the device is a touch device
function detectTouchDevice(): boolean {
  if (typeof window === 'undefined') return false;

  // Primary check - standard property
  const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  // Additional check for tablets, which may have touch but larger viewports
  const isTabletOS = /iPad|iPhone|iPod|Android/.test(navigator.userAgent);

  return hasTouch || isTabletOS;
}

// Helper hook for checking if viewport is above a certain breakpoint
export function useMediaQuery(breakpoint: Breakpoint): boolean {
  const { width } = useViewport();
  return width >= BREAKPOINTS[breakpoint];
}
